import {
  parseISO,
  format,
  formatDistanceToNow,
  isValid,
  differenceInCalendarDays,
  differenceInDays,
  isBefore,
  isSameDay,
} from "date-fns";

/**
 * Safely parse an ISO date string. Returns null if invalid.
 */
export function safeParseISO(dateString: string | null | undefined): Date | null {
  if (typeof dateString !== "string" || dateString.trim().length === 0) {
    return null;
  }
  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Safely parse a date from a string using new Date(). Returns null if invalid.
 * For date-only strings (YYYY-MM-DD), uses local timezone to avoid off-by-one errors.
 */
export function safeParseDateString(dateString: string | null | undefined): Date | null {
  if (typeof dateString !== "string" || dateString.trim().length === 0) {
    return null;
  }
  try {
    const trimmed = dateString.trim();
    // Date-only strings (YYYY-MM-DD): parse as local to avoid UTC midnight timezone drift
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      const parsed = new Date(year, month - 1, day);
      return isValid(parsed) ? parsed : null;
    }
    // Datetime strings without timezone (YYYY-MM-DDTHH:mm:ss): parse as local time.
    // new Date("2026-02-28T09:00:00") treats this as UTC in most browsers, causing
    // a timezone offset. Using the Date constructor with components keeps it local.
    const dtMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (dtMatch) {
      const [, yr, mo, dy, hr, mi, sc] = dtMatch;
      const parsed = new Date(+yr, +mo - 1, +dy, +hr, +mi, +(sc || 0));
      return isValid(parsed) ? parsed : null;
    }
    const parsed = new Date(dateString);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local Date to avoid timezone drift.
 * Falls back to safeParseDateString for other formats.
 */
export function parseLocalDate(dateString: string | null | undefined): Date | null {
  if (typeof dateString !== "string" || dateString.trim().length === 0) {
    return null;
  }

  const trimmed = dateString.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      isValid(parsed) &&
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed;
    }
    return null;
  }

  return safeParseDateString(trimmed);
}

/**
 * Parse an event datetime string from Supabase as a local time.
 * Supabase stores timezone-naive ISO strings like "2025-02-28T09:00:00"
 * These should be interpreted as local time, NOT UTC.
 *
 * Handles:
 * - Timezone-naive ISO strings: "2025-02-28T09:00:00" → local time
 * - With seconds: "2025-02-28T09:00:00"
 * - With milliseconds: "2025-02-28T09:00:00.123"
 *
 * Falls back to parseISO for timezone-aware strings (with Z or +/- offset).
 */
export function parseEventDateTime(dateString: string | null | undefined): Date | null {
  if (typeof dateString !== "string" || dateString.trim().length === 0) {
    return null;
  }

  const trimmed = dateString.trim();

  // Check if this is a timezone-aware string (has Z or +/- offset)
  const isTimezoneAware = trimmed.includes('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed) || /[+-]\d{4}$/.test(trimmed);

  if (isTimezoneAware) {
    // For timezone-aware strings, use standard ISO parsing (treats as UTC)
    return safeParseISO(trimmed);
  }

  // For timezone-naive strings from Supabase, treat as local time
  return safeParseDateString(trimmed);
}

/**
 * Safely format a date string with a given format pattern.
 * For time-of-day strings without timezone info (HH:mm format in ISO), treat as local time.
 * Returns fallback string if the date is invalid.
 */
export function safeFormat(
  dateString: string | null | undefined,
  formatPattern: string,
  fallback: string = "—"
): string {
  if (!dateString) return fallback;

  // Check if this looks like a time-of-day string (YYYY-MM-DDTHH:mm without timezone)
  // These should be treated as local time, not UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateString) && !dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    // This is a local time string without timezone info - parse as local
    const parsed = safeParseDateString(dateString);
    if (!parsed) return fallback;
    try {
      return format(parsed, formatPattern);
    } catch {
      return fallback;
    }
  }

  // Otherwise use standard ISO parsing
  const parsed = safeParseISO(dateString);
  if (!parsed) return fallback;
  try {
    return format(parsed, formatPattern);
  } catch {
    return fallback;
  }
}

/**
 * Safely format a date string with a given format pattern using new Date().
 * Returns fallback string if the date is invalid.
 */
export function safeFormatDate(
  dateString: string | null | undefined,
  formatPattern: string,
  fallback: string = "—"
): string {
  const parsed = safeParseDateString(dateString);
  if (!parsed) return fallback;
  try {
    return format(parsed, formatPattern);
  } catch {
    return fallback;
  }
}

/**
 * Safely format distance to now from a date string.
 * Returns fallback string if the date is invalid.
 */
export function safeFormatDistanceToNow(
  dateString: string | null | undefined,
  options?: { addSuffix?: boolean },
  fallback: string = "—"
): string {
  const parsed = safeParseDateString(dateString);
  if (!parsed) return fallback;
  try {
    return formatDistanceToNow(parsed, options);
  } catch {
    return fallback;
  }
}

/**
 * Safely calculate difference in days between a date string and now.
 * Returns null if the date is invalid.
 */
export function safeDifferenceInDays(
  dateString: string | null | undefined,
  baseDate: Date = new Date()
): number | null {
  const parsed = safeParseISO(dateString);
  if (!parsed) return null;
  try {
    return differenceInDays(parsed, baseDate);
  } catch {
    return null;
  }
}

/**
 * Check if a date string represents a date within the last N days.
 * Uses parseLocalDate for date-only strings to avoid timezone drift.
 */
export function isWithinDays(
  dateString: string | null | undefined,
  days: number
): boolean {
  // Prefer parseLocalDate for date-only strings to avoid off-by-one from UTC midnight
  const parsed = parseLocalDate(dateString) ?? safeParseISO(dateString);
  if (!parsed) return false;
  try {
    const diff = differenceInDays(new Date(), parsed);
    return diff >= 0 && diff <= days;
  } catch {
    return false;
  }
}

/**
 * Returns today's date as a YYYY-MM-DD string in the user's LOCAL
 * calendar (not UTC). Use this for `_as_of`-style defaults where
 * "today" should match the user's local-day boundary.
 *
 * UTC alternatives like `new Date().toISOString().slice(0, 10)` shift
 * the cutoff by up to 24 hours for users far from UTC, breaking
 * "as of today" reports near midnight.
 */
export function localDateYmd(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * A UTC instant's clock time in an IANA timezone, as "9:30 AM" (date-fns's
 * "h:mm a"), so a timed event reads the same time for every viewer rather than
 * the viewer's browser time (#6410). date-fns's format() has no timezone
 * parameter. A blank or invalid zone falls back to America/New_York, as
 * timeZoneDateYmd does. "en-US" keeps Latin digits and the AM/PM suffix
 * whatever the viewer's own locale is.
 */
export function formatTimeInTimeZone(
  timeZone: string | null | undefined,
  date: Date,
  fallbackTimeZone = "America/New_York",
): string {
  const formatInZone = (zone: string) =>
    new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(date);
  try {
    return formatInZone(timeZone?.trim() || fallbackTimeZone);
  } catch {
    return formatInZone(fallbackTimeZone);
  }
}

/**
 * Returns a YYYY-MM-DD calendar value in an IANA timezone. Use this for
 * organization-scoped dates that must agree with server-side date guards.
 */
export function timeZoneDateYmd(
  timeZone: string | null | undefined,
  date: Date = new Date(),
  fallbackTimeZone = "America/New_York",
): string {
  const formatInZone = (zone: string) => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const values = Object.fromEntries(
      parts
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, part.value]),
    );
    return `${values.year}-${values.month}-${values.day}`;
  };

  try {
    return formatInZone(timeZone?.trim() || fallbackTimeZone);
  } catch {
    try {
      return formatInZone(fallbackTimeZone);
    } catch {
      return localDateYmd(date);
    }
  }
}

/**
 * Inverse of `timeZoneDateYmd`: given a "YYYY-MM-DD" calendar date and an IANA
 * timezone, returns the UTC instant for local midnight on that date in that
 * timezone. Use this to store an all-day (or otherwise date-only, timezone-
 * agnostic) value as a `timestamptz` without the result depending on the
 * browser timezone of whoever happens to be creating it.
 */
/**
 * Shared engine behind zonedMidnightToUtcIso and zonedDateTimeToUtcIso --
 * the iterative DST-correction approach documented on zonedMidnightToUtcIso
 * itself, generalized to an arbitrary hour/minute/second instead of always
 * 0/0/0.
 */
function zonedYmdHmsToUtcIso(
  y: number,
  m: number, // 1-indexed
  d: number,
  hh: number,
  mm: number,
  ss: number,
  timeZone: string | null | undefined,
  fallbackTimeZone: string,
): string {
  const targetUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss);

  const toIsoInZone = (zone: string): string => {
    // Treat the desired wall-clock date as if it were UTC, then measure how
    // far that guess drifts when rendered in `zone`, and correct for the
    // drift. A single correction can overshoot across a DST transition near
    // the target date (the offset at the *guess* instant differs from the
    // offset at the *corrected* instant), so iterate until the wall time
    // this instant renders in `zone` exactly matches the target — in
    // practice this converges within one extra pass even at a transition.
    //
    // Some zones (e.g. America/Santiago) advance their clocks exactly at
    // local midnight, so local "00:00" on the transition date never exists
    // at all — the wall clock jumps straight from 23:59 to 01:00. No
    // instant renders exactly at the target there, so the correction
    // oscillates between the instant just before the gap (which renders on
    // the *previous* calendar date) and just after it, never reaching a
    // diff of zero. Track the smallest non-negative diff seen — that
    // candidate's own (uncorrected) instant already renders on-or-after the
    // target time on the *correct* calendar date, which is what a date-only
    // value needs, even though it isn't exactly local midnight.
    let candidateMs = targetUtcMs;
    let bestForwardMs: number | null = null;
    let bestForwardDiff = Infinity;
    for (let i = 0; i < 4; i++) {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).formatToParts(new Date(candidateMs));
      const values = Object.fromEntries(
        parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
      );
      const hour = values.hour === "24" ? 0 : Number(values.hour);
      const renderedAsUtcMs = Date.UTC(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        hour,
        Number(values.minute),
        Number(values.second),
      );
      const diffMs = renderedAsUtcMs - targetUtcMs;
      if (diffMs === 0) return new Date(candidateMs).toISOString();
      if (diffMs > 0 && diffMs < bestForwardDiff) {
        bestForwardDiff = diffMs;
        bestForwardMs = candidateMs;
      }
      candidateMs -= diffMs;
    }
    return new Date(bestForwardMs ?? candidateMs).toISOString();
  };

  try {
    return toIsoInZone(timeZone?.trim() || fallbackTimeZone);
  } catch {
    try {
      return toIsoInZone(fallbackTimeZone);
    } catch {
      return new Date(Date.UTC(y, m - 1, d, hh, mm, ss)).toISOString();
    }
  }
}

export function zonedMidnightToUtcIso(
  dateStr: string,
  timeZone: string | null | undefined,
  fallbackTimeZone = "America/New_York",
): string | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  return zonedYmdHmsToUtcIso(+y, +m, +d, 0, 0, 0, timeZone, fallbackTimeZone);
}

/**
 * Converts a wall-clock date + time-of-day in `timeZone` to the equivalent
 * UTC instant — e.g. a service's own scheduled_date + start_time/end_time,
 * which are stored as plain, offset-less values meant to be read in the
 * ORGANIZATION's timezone, not whichever timezone happens to parse a bare
 * "YYYY-MM-DDTHH:MM:SS" string (a member's own browser-local zone, per
 * `new Date()`'s no-designator parsing rule — Codex P2, PR #6383 round 7).
 * `timeStr` accepts "HH:MM" or "HH:MM:SS". Same DST-correction approach as
 * zonedMidnightToUtcIso above — see that function's own doc.
 */
export function zonedDateTimeToUtcIso(
  dateStr: string,
  timeStr: string,
  timeZone: string | null | undefined,
  fallbackTimeZone = "America/New_York",
): string | null {
  const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return null;
  const timeMatch = timeStr.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!timeMatch) return null;
  const [, y, m, d] = dateMatch;
  const [, hh, mm, ss] = timeMatch;
  return zonedYmdHmsToUtcIso(+y, +m, +d, +hh, +mm, ss ? +ss : 0, timeZone, fallbackTimeZone);
}

/**
 * Companion to `zonedMidnightToUtcIso`: the UTC instant one millisecond
 * before midnight the following day in `timeZone` — i.e. the last instant
 * of `dateStr`'s calendar day in that timezone, for a date-only value's
 * `end_time`. Subtracting a full minute instead of one millisecond would
 * make this exactly 23:59:00, which every instant-based consumer (the
 * public-events retention query, useMemberNextUp, the kiosk/check-in
 * "today" upper bound, etc.) would then treat as "already finished" for
 * the entire 23:59:00-23:59:59 window of the event's actual last day
 * (Codex round-16 finding on #5782).
 */
export function zonedEndOfDayToUtcIso(
  dateStr: string,
  timeZone: string | null | undefined,
  fallbackTimeZone = "America/New_York",
): string | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  // Calendar-date-only arithmetic in UTC has no DST, so this is safe purely
  // as a way to get "the next calendar date" string.
  const nextDay = new Date(Date.UTC(+y, +m - 1, +d + 1));
  const nextDateStr = `${nextDay.getUTCFullYear()}-${String(nextDay.getUTCMonth() + 1).padStart(2, "0")}-${String(nextDay.getUTCDate()).padStart(2, "0")}`;
  const nextMidnightIso = zonedMidnightToUtcIso(nextDateStr, timeZone, fallbackTimeZone);
  if (!nextMidnightIso) return null;
  return new Date(new Date(nextMidnightIso).getTime() - 1).toISOString();
}

/**
 * The set of recurrence units all-day event expansion supports. Kept as a
 * plain string union here (rather than importing the feature-level
 * `RecurrenceUnit` type from calendarTypes.ts) so this stays a dependency-free
 * date utility usable from any recurrence-expansion implementation.
 */
export type DateOnlyRecurrenceUnit = "daily" | "weekly" | "biweekly" | "monthly" | "quarterly" | "yearly";

const formatYmd = (y: number, m0: number, d: number) =>
  `${y}-${String(m0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/**
 * Add `monthsToAdd` calendar months to (y, zero-indexed m0, d), clamping to
 * the last valid day of the target month when the original day doesn't
 * exist there — matching date-fns `addMonths`' behavior (e.g. Jan 31 + 1
 * month = Feb 28, not a rollover into March). A raw `setUTCMonth` call does
 * not clamp: it rolls the excess days into the following month instead.
 */
const addUtcMonthsClamped = (y: number, m0: number, d: number, monthsToAdd: number) => {
  const totalMonths = m0 + monthsToAdd;
  const targetYear = y + Math.floor(totalMonths / 12);
  const targetMonth0 = ((totalMonths % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth0 + 1, 0)).getUTCDate();
  return formatYmd(targetYear, targetMonth0, Math.min(d, lastDayOfTargetMonth));
};

/**
 * Advance a "YYYY-MM-DD" calendar date by one recurrence unit, using pure
 * calendar-date arithmetic (UTC-backed only so browser DST never enters the
 * calculation) rather than adding a fixed duration to an instant. Fixed-ms
 * advancement is correct for timed events — a weekly 9am event should stay
 * at 9am wall-clock even across a DST change — but an all-day event has no
 * wall-clock time to preserve, and advancing its instant by exactly 7*24h
 * can cross a DST boundary and land on the wrong calendar date in the
 * organization's timezone.
 */
export const advanceYmd = (dateStr: string, unit: DateOnlyRecurrenceUnit): string => {
  const [y, m, d] = dateStr.split("-").map(Number);
  switch (unit) {
    case "daily":
    case "weekly":
    case "biweekly": {
      const days = unit === "daily" ? 1 : unit === "weekly" ? 7 : 14;
      const date = new Date(Date.UTC(y, m - 1, d));
      date.setUTCDate(date.getUTCDate() + days);
      return formatYmd(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    }
    case "monthly": return addUtcMonthsClamped(y, m - 1, d, 1);
    case "quarterly": return addUtcMonthsClamped(y, m - 1, d, 3);
    case "yearly": return addUtcMonthsClamped(y, m - 1, d, 12);
    default: return dateStr;
  }
};

/**
 * Number of calendar days from `startYmd` to `endYmd` ("YYYY-MM-DD" each),
 * computed via UTC-backed calendar arithmetic (no DST) rather than an
 * instant subtraction, so it reflects the day span a multi-day all-day
 * event actually spans regardless of either date's timezone anchoring.
 */
export const ymdDiffDays = (startYmd: string, endYmd: string): number => {
  const [sy, sm, sd] = startYmd.split("-").map(Number);
  const [ey, em, ed] = endYmd.split("-").map(Number);
  const startMs = Date.UTC(sy, sm - 1, sd);
  const endMs = Date.UTC(ey, em - 1, ed);
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000));
};

/**
 * Add `days` calendar days to a "YYYY-MM-DD" date, using the same UTC-backed
 * arithmetic as `advanceYmd`.
 */
export const addDaysToYmd = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return formatYmd(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * Format a Date for date-only filters (local timezone).
 */
export function formatDateForFilter(date: Date | null | undefined): string | null {
  if (!date || !isValid(date)) {
    return null;
  }
  try {
    return format(date, "yyyy-MM-dd");
  } catch {
    return null;
  }
}

// ============================================================
// Shared Display Formatting
// ============================================================
// Use these functions across the entire codebase to ensure
// consistent date rendering. Each function handles null/invalid
// inputs gracefully, returning a fallback string ("—").

/**
 * Safely convert any date-like input to a Date object.
 * Returns null if the value is missing or invalid.
 */
function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  return safeParseDateString(value);
}

/**
 * Normalize a date value to a Date object. Returns null for invalid/missing values.
 * Used by aggregation modules for consistent date handling.
 */
export function normalizeDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Calendar days from `today` to a due date: negative when overdue, 0 when due
 * today, positive when still ahead. null when the value is missing or not a
 * date.
 *
 * A "YYYY-MM-DD" due date (tasks.due_date, requests.due_at) is a calendar day,
 * so it is read as that day in the viewer's time zone. `new Date("YYYY-MM-DD")`
 * is UTC midnight, which is the previous evening in the US: every task due
 * today read as overdue, and one due tomorrow read as due today.
 */
export function dueDayOffset(
  dueDate: string | Date | null | undefined,
  today: Date = new Date(),
): number | null {
  const parsed = toDate(dueDate);
  if (!parsed) return null;
  return differenceInCalendarDays(parsed, today);
}

/**
 * Whether a date-only value falls on or between two date-only bounds, all as
 * "YYYY-MM-DD" calendar days (an empty bound is open). Compared as text on
 * purpose: parsing them with new Date() puts the value at UTC midnight, which
 * is the evening before in the US, so a service on the start day fell outside
 * the range and one the day after the end day fell inside it.
 */
export function isWithinYmdRange(
  day: string,
  startYmd?: string | null,
  endYmd?: string | null,
): boolean {
  const value = day.slice(0, 10);
  if (startYmd && value < startYmd) return false;
  if (endYmd && value > endYmd) return false;
  return true;
}

/**
 * The first day of a month, as a local Date, from a "YYYY-MM" month key or a
 * "YYYY-MM-DD" date. `new Date("2026-03")` and `new Date("2026-03-01")` are UTC
 * midnight, the evening of Feb 28 in the US, so a March label read "February".
 * Timestamps go through the normal parser. null when it isn't a date.
 */
export function parseMonthStart(value: string | null | undefined): Date | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const month = /^(\d{4})-(\d{2})$/.exec(trimmed);
  if (month) return parseLocalDate(`${month[1]}-${month[2]}-01`);
  return safeParseDateString(trimmed);
}

/**
 * The calendar year of a date-only value ("2026-01-01" is 2026), or null. A
 * Jan 1 date parsed as UTC is Dec 31 of the year before in the US.
 */
export function getCalendarYear(value: string | null | undefined): number | null {
  return parseLocalDate(value)?.getFullYear() ?? null;
}

/**
 * The latest of several date-only values as a local Date, ignoring missing or
 * unreadable ones; null when none is readable. Comparing and then formatting
 * `new Date("YYYY-MM-DD")` instants shows the day before in the US.
 */
export function latestDateOnly(values: ReadonlyArray<string | null | undefined>): Date | null {
  let latest: Date | null = null;
  for (const value of values) {
    const parsed = parseLocalDate(value);
    if (parsed && (latest === null || parsed > latest)) latest = parsed;
  }
  return latest;
}

/**
 * Compact date for lists, queues, and compact views: "Jan 6"
 */
export function formatCompactDate(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "MMM d");
  } catch {
    return fallback;
  }
}

/**
 * Standard date with year: "Jan 6, 2024"
 */
export function formatDateWithYear(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "MMM d, yyyy");
  } catch {
    return fallback;
  }
}

/**
 * Date with weekday: "Mon, Jan 6"
 */
export function formatDateWithWeekday(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "EEE, MMM d");
  } catch {
    return fallback;
  }
}

/**
 * Full header date: "Monday, January 6"
 */
export function formatFullHeaderDate(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "EEEE, MMMM d");
  } catch {
    return fallback;
  }
}

/**
 * Month and year: "Jan 2024"
 */
export function formatMonthYear(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "MMM yyyy");
  } catch {
    return fallback;
  }
}

/**
 * Full date-time for tooltips and audit trails: "Jan 6, 2024, 2:30 PM"
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "MMM d, yyyy, h:mm a");
  } catch {
    return fallback;
  }
}

/**
 * Time only: "2:30 PM"
 */
export function formatTime(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;
  try {
    return format(parsed, "h:mm a");
  } catch {
    return fallback;
  }
}

/**
 * Compact relative time: "just now", "5m ago", "2h ago", "3d ago".
 * For badges, queue items, and compact timestamps.
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;

  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();

  if (diffMs < 0) return "just now";

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

/**
 * Verbose relative time: "Just now", "Yesterday", "2 days ago".
 * For request cards and longer descriptions.
 */
export function formatTimeAgo(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;

  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return diffDays === 1 ? "Yesterday" : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }
  return "Just now";
}

/**
 * Due date label: "Due today", "Past due since Jan 6", "Due Jan 6", "No due date".
 * For work queue items, task lists, and request cards.
 */
export function formatDueLabel(
  dueDate: string | Date | null | undefined,
  today: Date = new Date(),
): string {
  if (!dueDate) return "No due date";
  const parsed = toDate(dueDate);
  if (!parsed) return "No due date";

  if (isSameDay(parsed, today)) return "Due today";
  if (isBefore(parsed, today)) return `Past due since ${format(parsed, "MMM d")}`;
  return `Due ${format(parsed, "MMM d")}`;
}

/**
 * Smart due date for dashboards: "Today", "Tomorrow", "Mon, Jan 6".
 */
export function formatDueDate(
  date: string | Date | null | undefined,
  fallback = "—",
): string {
  const parsed = toDate(date);
  if (!parsed) return fallback;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);

  if (normalized.getTime() === today.getTime()) return "Today";
  if (normalized.getTime() === tomorrow.getTime()) return "Tomorrow";

  try {
    return format(parsed, "EEE, MMM d");
  } catch {
    return fallback;
  }
}
